import React, { useEffect, useState } from 'react';
import {
    IonButton,
    IonCheckbox,
    IonIcon,
    IonLabel,
    IonPopover,
    IonSelect,
    IonSelectOption
} from '@ionic/react';
import { cartOutline } from 'ionicons/icons';
import createAxiosInstance from "../../services/AxiosInstance";
import { useToast } from "../../utils/useToasts";
import './DrinkStripesPopOver.css';
import LoadingSpinner from "../shared/LoadingSpinner";
import {DertigenEvent} from "../../model/DertigenEvent";
import {DertigenPlayer} from "../../model/DertigenPlayer";
import {buildFriendHelpMove} from "../../utils/dertigenMovePayloads";

interface DrinkStripesPopOverProps {
    isOpen: boolean;
    onClose: () => void;
    selectedPlayer?: DertigenPlayer;
    playersInLobby?: DertigenPlayer[];
    lobbyId?: number;
}

const METHOD_NOT_ALLOWED_STATUS = 405;
const STRIPE_AMOUNTS = {
    halfBeer: 10,
    fullBeer: 20,
    submarine: 25,
    rietadt: 30,
} as const;

const DrinkStripesPopOver: React.FC<DrinkStripesPopOverProps> = ({
                                                                     isOpen,
                                                                     onClose,
                                                                     selectedPlayer,
                                                                     playersInLobby,
                                                                     lobbyId,
                                                                 }) => {
    const axios = createAxiosInstance();
    const showToasts = useToast();

    const [player, setPlayer] = useState<DertigenPlayer>();
    const [selectedOption, setSelectedOption] = useState<string>('');
    const [loading, setLoading] = useState<boolean>(true);
    const [processing, setProcessing] = useState<boolean>(false);

    const isGuestPlayer = !!player?.roles &&
        !player.roles.includes('user') &&
        !player.roles.includes('dertigen-user');
    const canBuyBack = !!player && isGuestPlayer && player.stripes < 1;

    useEffect(() => {
        if (isOpen && lobbyId && selectedPlayer?.id) {
            const fetchPlayerData = async () => {
                setLoading(true);
                try {
                    const res = await axios.get(`/lobbies/${lobbyId}/${selectedPlayer.id}`);
                    setPlayer(res.data);
                } catch (error: any) {
                    console.error(error);
                    showToasts('Er is iets mis gegaan bij het laden van de data', 'danger');
                } finally {
                    setLoading(false);
                }
            };
            fetchPlayerData();
        }
    }, [isOpen, lobbyId, selectedPlayer?.id]);

    const handleDrink = async (amount: number) => {
        if (!lobbyId || !selectedPlayer?.id) return;
        if (processing) return;
        setProcessing(true);
        try {
            let res;
            if (!selectedOption) {
                const move : DertigenEvent = {
                    stripes: Number(-amount),
                    gulps: amount,
                }
                res = await axios.patch(`/lobbies/move/${lobbyId}/${selectedPlayer.id}`, move);
                showToasts(`Je hebt ${amount} strepen gedronken`, "success");
            } else {
                const move = buildFriendHelpMove(amount);
                res = await axios.patch(`/lobbies/help-friend/${lobbyId}/${selectedPlayer.id}/${selectedOption}`, move);
                showToasts(`Een friend heeft geholpen met ${amount} slokjes!`, "success");
                setSelectedOption('');
            }
            setPlayer(res.data);
        } catch (err: any) {
            console.error(err);
            if (err.response?.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', "danger");
            } else {
                showToasts('Er is iets mis gegaan', "danger");
            }
        } finally {
            setProcessing(false);
        }
    };

    const handleSelectChange = (event: CustomEvent) => {
        setSelectedOption(event.detail.value);
    };

    const handleCheckboxChange = (event: CustomEvent) => {
        if (!event.detail.checked) {
            setSelectedOption('');
        }
    };

    const handleClose = () => {
        onClose();
        setSelectedOption('');
    };

    const handleBuyBack = async () => {
        if (!lobbyId || !player?.id) return;
        if (processing) return;
        setProcessing(true);
        try {
            await axios.patch(`/lobbies/buy-back/${lobbyId}?userId=${player.id}`);
            const refreshed = await axios.get(`/lobbies/${lobbyId}/${player.id}`);
            setPlayer(refreshed.data);
            showToasts('Speler is opnieuw ingekocht', 'success');
        } catch (err: any) {
            console.error(err);
            if (err.response?.status === METHOD_NOT_ALLOWED_STATUS) {
                showToasts('Je hebt geen toestemming', 'danger');
            } else {
                showToasts('Er is iets mis gegaan', 'danger');
            }
        } finally {
            setProcessing(false);
        }
    };

    return (
        <IonPopover isOpen={isOpen} onDidDismiss={handleClose} className="popover-layout">
            {loading ? (
                <LoadingSpinner />
            ) : (
                <div className="drink-container">

                    <IonLabel className="ion-text-center">
                        <h1>
                            {player?.name}: {player?.stripes} strepen
                        </h1>
                    </IonLabel>

                    <div className="add-stripes-grid">
                        <div className="custom-row ion-color-success">
                            <div className="custom-col ion-text-center">
                                <IonButton
                                    className={selectedOption ? 'green' : 'normal'}
                                    onClick={() => handleDrink(STRIPE_AMOUNTS.halfBeer)}
                                    disabled={processing || canBuyBack}
                                >
                                    Halve bak
                                </IonButton>
                            </div>
                            <div className="custom-col ion-text-center">
                                <IonButton
                                    className={selectedOption ? 'green' : 'normal'}
                                    onClick={() => handleDrink(STRIPE_AMOUNTS.fullBeer)}
                                    disabled={processing || canBuyBack}
                                >
                                    Hele bak
                                </IonButton>
                            </div>
                        </div>
                        <div className="custom-row">
                            <div className="custom-col ion-text-center">
                                <IonButton
                                    className={selectedOption ? 'green' : 'normal'}
                                    onClick={() => handleDrink(STRIPE_AMOUNTS.submarine)}
                                    disabled={processing || canBuyBack}
                                >
                                    Duikboot
                                </IonButton>
                            </div>
                            <div className="custom-col ion-text-center">
                                <IonButton
                                    className={selectedOption ? 'green' : 'normal'}
                                    onClick={() => handleDrink(STRIPE_AMOUNTS.rietadt)}
                                    disabled={processing || canBuyBack}
                                >
                                    Rietadt
                                </IonButton>
                            </div>
                        </div>
                        {canBuyBack && (
                        <button
                            className="buy-back-corner-btn"
                            onClick={handleBuyBack}
                            disabled={processing}
                            title="Opnieuw inkopen"
                        >
                            <IonIcon icon={cartOutline} />
                            <span>Terug inkopen</span>
                        </button>
                    )}
                        <div className="custom-item">
                            <IonCheckbox
                                slot="start"
                                checked={!!selectedOption}
                                onIonChange={handleCheckboxChange}
                                disabled={!selectedOption || canBuyBack}
                            />
                            <IonSelect
                                placeholder="Wie drinkt er voor deze persoon?"
                                value={selectedOption}
                                interface="action-sheet"
                                onIonChange={handleSelectChange}
                                fill="outline"
                                className="custom-select"
                                disabled={canBuyBack}
                            >
                                {playersInLobby
                                    ?.filter((p: DertigenPlayer) => p.id !== player?.id)
                                    .map((p: DertigenPlayer) => (
                                        <IonSelectOption key={p.id} value={p.id} className="ion-select-option-class custom-select-option">
                                            {p.name}
                                        </IonSelectOption>
                                    ))}
                            </IonSelect>
                        </div>
                    </div>
                </div>
            )}
        </IonPopover>
    );
};

export default DrinkStripesPopOver;
